import React from 'react';
import { CTASection } from '../../components/CTASection';
import { FAQSection } from '../../components/FAQSection';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { TrendingUp, FileText } from 'lucide-react';
import { useSite } from '../../contexts/SiteContext';

export const MDI = () => {
  const { theme } = useSite();
  
  const faqs = [
    { question: 'What is multidirectional instability?', answer: 'MDI is symptomatic shoulder instability in more than one direction (anterior, posterior, inferior). Unlike traumatic instability, it often develops gradually in patients with generalized ligament laxity. Rehabilitation is the primary treatment.' },
    { question: 'Who gets MDI?', answer: 'Common in: young females with hyperlaxity, overhead athletes (swimmers, volleyball players), patients with connective tissue disorders (Ehlers-Danlos), and those who develop instability from repetitive microtrauma rather than a single injury.' },
    { question: 'How is MDI treated?', answer: 'Rehabilitation is first-line treatment and successful in 80-90% of cases. Focus on rotator cuff and scapular strengthening, proprioception, and avoiding positions of instability. Surgery (capsular shift) is reserved for patients failing 6+ months of dedicated physiotherapy.' },
    { question: 'Is surgery effective?', answer: 'Surgery (arthroscopic or open capsular shift) is effective in 80-90% of appropriately selected patients. Key to success is patient selection (true MDI, failed adequate rehabilitation) and addressing all directions of instability during surgery.' }
  ];

  return (
    <div className="min-h-screen" data-testid="mdi-page">
      <section className="bg-gradient-to-br from-orange-50 to-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <span className="inline-block px-4 py-1 rounded-full text-sm font-medium mb-4" style={{ backgroundColor: `${theme?.primaryHex}20`, color: theme?.primaryHex }}>
              Instability & Sports
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Multidirectional Shoulder Instability (MDI) Treatment in Hyderabad</h1>
            <p className="text-xl text-gray-600">Diagnosis & rehabilitation-first treatment by Dr. Deepthi Nandan Reddy</p>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Understanding MDI</h2>
            <div className="prose prose-lg max-w-none text-gray-700 space-y-4">
              <p>MDI is characterized by the AMBRII mnemonic: <strong>A</strong>traumatic, <strong>M</strong>ultidirectional, often <strong>B</strong>ilateral, <strong>R</strong>ehabilitation is primary treatment, <strong>I</strong>nferior capsular shift if surgery needed, <strong>I</strong>nterval closure.</p>
              <p><strong>Key Features:</strong> Positive sulcus sign, generalized ligament laxity, bilateral symptoms, gradual onset without specific trauma.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <TrendingUp className="w-8 h-8" style={{ color: theme?.primaryHex }} />
              <h2 className="text-3xl font-bold text-gray-900">Treatment Protocol</h2>
            </div>
            <div className="space-y-4">
              <Card className="border-l-4 border-l-green-500">
                <CardHeader><CardTitle className="flex items-center gap-2"><span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded">FIRST LINE - 6+ MONTHS</span>Rehabilitation</CardTitle></CardHeader>
                <CardContent className="text-gray-700">
                  <p>• Rotator cuff strengthening (especially external rotators)</p>
                  <p>• Scapular stabilization (critical component)</p>
                  <p>• Proprioceptive training</p>
                  <p>• Avoid positions of instability</p>
                  <p>• Core strengthening</p>
                  <p className="bg-green-50 p-3 rounded mt-3 text-sm">Success rate: 80-90% with dedicated program lasting 6+ months</p>
                </CardContent>
              </Card>
              <Card className="border-l-4 border-l-orange-500">
                <CardHeader><CardTitle className="flex items-center gap-2"><span className="bg-orange-100 text-orange-800 text-xs px-2 py-1 rounded">IF REHAB FAILS</span>Surgical Options</CardTitle></CardHeader>
                <CardContent className="text-gray-700">
                  <p>• Arthroscopic capsular plication (tightening)</p>
                  <p>• Open inferior capsular shift (Neer procedure)</p>
                  <p>• Rotator interval closure</p>
                  <p>• Address all directions of instability</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <FAQSection faqs={faqs} />
      <CTASection />
    </div>
  );
};

export default MDI;
